import React from 'react';

import { getContainerStyle, getMessageStyle } from './styles.ts';

import { MessageUI } from '@/api/messaging/types.ts';
import { MessageTypes } from '@/enums/apiEnums.ts';
import { ViewMessageType } from '@/enums/viewEnums.ts';
import { MessageStatus } from '@/components/Messanger/Message/MessageItem/MessageStatus.tsx';
import MessageContent from '@/components/Messanger/Message/MessageItem/MessageContent.tsx';
import { ReplyMessage } from '@/components/Messanger/Message/ReplyMessage/ReplyMessage.tsx';
import Reply from '@/components/common/svg/Reply.tsx';
import { getMessageType } from '@/pages/Messenger/utils/common.ts';
import { useSwipeToReply } from '@/hooks/useSwipeToReply.ts';

interface MessageItemProps {
  message: MessageUI;
  onResend?: (messageId: string) => void;
  onReply?: (message: MessageUI) => void;
  isHighlighted?: boolean;
  onReplyMessageClick?: (messageId: string) => void;
  contactName?: string;
  contentRef?: React.RefObject<HTMLDivElement>;
}

export const MessageItem = ({
  message,
  onResend,
  onReply,
  isHighlighted,
  onReplyMessageClick,
  contactName,
  contentRef
}: MessageItemProps) => {
  const messageType: ViewMessageType = getMessageType(
    message.labels,
    message.type
  );

  const isSwipeDisabled =
    messageType === ViewMessageType.SYSTEM ||
    messageType === ViewMessageType.MATCH ||
    message.type === MessageTypes.TYPE_STICKER ||
    message.type === MessageTypes.TYPE_ICEBREAKER_GIFT ||
    message.type === MessageTypes.TYPE_BAIT;

  const {
    swipeOffset,
    isSwiping,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd
  } = useSwipeToReply({
    message,
    isSwipeDisabled,
    onReply,
    contentRef
  });

  const getMessageContainerStyle = () => {
    if (
      message.type === MessageTypes.TYPE_STICKER ||
      message.type === MessageTypes.TYPE_ICEBREAKER_GIFT
    ) {
      return {}; // no BG for gifts and stickers
    }

    const shouldUseColumn =
      message.reply_message ||
      ((message.text_body?.message?.length ??
        message.bait_body?.message?.length) ||
        0) > 30;

    return {
      ...getMessageStyle(messageType),
      flexDirection: shouldUseColumn ? 'column' : 'row'
    };
  };

  const containerStyle = getContainerStyle(messageType);

  return (
    <>
      <div
        style={{
          position: 'relative',
          overflow: 'visible',
          isolation: 'isolate',
          width: '100%',
          display: 'flex',
          margin: containerStyle.margin
        }}
      >
        <div
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            width: '100%',
            justifyContent: containerStyle.justifyContent,
            transform: `translateX(-${swipeOffset}px)`,
            transition: isSwiping ? 'none' : 'transform 0.3s ease',
            willChange: isSwiping ? 'transform' : 'auto',
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden'
          }}
        >
          <div
            style={{
              ...getMessageContainerStyle(),
              position: 'relative',
              isolation: 'isolate'
            }}
          >
            {isHighlighted && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  backgroundColor: 'rgba(252,192,45,0.15)',
                  borderRadius: 'inherit',
                  pointerEvents: 'none',
                  transition: 'opacity 0.3s ease',
                  zIndex: 0
                }}
              />
            )}
            {message.reply_message && (
              <ReplyMessage
                replyMessage={message.reply_message}
                contactName={contactName}
                onReplyClick={() =>
                  onReplyMessageClick?.(message.reply_message!.create_micros)
                }
                showCancelButton={false}
              />
            )}
            <div
              style={{
                display: 'flex',
                flexDirection:
                  ((message.text_body?.message?.length ??
                    message.bait_body?.message?.length) ||
                    0) > 30
                    ? 'column'
                    : 'row',
                alignItems: message.reply_message ? 'flex-start' : 'flex-end',
                textAlign: 'left',
                alignSelf: message.reply_message ? 'flex-start' : 'auto'
              }}
            >
              <MessageContent message={message} messageType={messageType} />
            </div>
            <MessageStatus
              messageType={messageType}
              message={message}
              onResend={onResend}
            />
          </div>
        </div>

        {swipeOffset > 0 && (
          <div
            style={{
              position: 'absolute',
              right: `${-40 + Math.min(swipeOffset, 40)}px`,
              top: '50%',
              transform: `translateY(-50%) scale(${Math.min(swipeOffset / 40, 1)})`,
              backgroundColor: '#9f9f9f',
              borderRadius: '50%',
              width: '28px',
              height: '28px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'none',
              transition: isSwiping
                ? 'none'
                : 'right 0.3s ease, opacity 0.3s ease, transform 0.3s ease',
              whiteSpace: 'nowrap',
              transformOrigin: 'center',
              opacity: Math.min(swipeOffset / 40, 1)
            }}
          >
            <Reply fillColor="#fff" width={12} height={12} />
          </div>
        )}
      </div>
    </>
  );
};
